import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@hotjar/hotsauce';
import type { CompanyDetails } from '@hotjar/webapp/domains/billing/invoice';
import { getCompanyCountryName } from '@hotjar/webapp/domains/billing/invoice';
import { getAngularService } from '@hotjar/webapp/utils/angular-injector';

import * as S from './InvoiceDetails.styles';

interface InvoiceDetailsProps {
  companyDetails: CompanyDetails;
  onEdit: (companyDetails: CompanyDetails) => void;
}

const emptyField = (
  <em>
    <Trans i18nKey="common:billing.invoice-details.empty-field">Not specified</Trans>
  </em>
);

export const InvoiceDetails = ({ companyDetails, onEdit }: InvoiceDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { company, vatNumber, address } = companyDetails;
  const countryName = getCompanyCountryName(companyDetails);
  const ngVatService = getAngularService('VAT');

  const isVatCountry = ngVatService.appliesToCountry(companyDetails.countryCode);

  const shouldShowAddress: boolean = !!address || !!countryName;

  return (
    <fieldset>
      <legend>{t('common:billing.invoice-details.title')}</legend>
      <S.FieldsetBody>
        <div>{t('common:billing.invoice-details.description')}</div>
        <div>
          <S.FieldTitle>{t('common:billing.invoice-details.company-label')}</S.FieldTitle>
          {company ? <span>{company}</span> : emptyField}
        </div>
        <div>
          <S.FieldTitle>{t('common:billing.invoice-details.address-label')}</S.FieldTitle>
          {shouldShowAddress ? (
            <>
              <S.Address>{address}</S.Address>
              <span>{countryName}</span>
            </>
          ) : (
            emptyField
          )}
        </div>
        {isVatCountry && (
          <div>
            <S.FieldTitle>{t('common:billing.invoice-details.vat-number-label')}</S.FieldTitle>
            {vatNumber ? <span>{vatNumber}</span> : emptyField}
          </div>
        )}
        <Button kind="secondary" size="s" onClick={() => onEdit(companyDetails)}>
          {t('common:billing.invoice-details.edit-button')}
        </Button>
      </S.FieldsetBody>
    </fieldset>
  );
};
